import tenantConstants from '@constants';
import TenantComponents from '@components';
import tenantTheme from '@theme';
import { Collapse, Space, Typography } from 'antd';
import cx from 'clsx';
import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useCreateCartMutation } from '../../../../apis/cart';
import { useApplyProductMutation } from '../../../../apis/listings';
import { useGetUpsellDetailQuery } from '../../../../apis/postlisting';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Flex,
  Heading,
  Icon,
  notification,
  ProductTag,
  Skeleton,
  Spinner,
  Text,
} from '../../../../components/common';
import { ActionButton } from '../../../../container/pages/user-settings/style';
import { Main } from '../../../../container/styled';
import { useRouteNavigate } from '../../../../hooks';
import UpsellDesktop from '../../../../static/lottie/bayutUpsellDesktop.json';
import UpsellMobile from '../../../../static/lottie/bayutUpsellMobile.json';
import { getAppSource } from '../../../../store/parentApi';
import { allocateCartLineQuantities, capitalizeFirstLetter, formatPrice } from '../../../../utility/utility';
import { isUnifiedProfolioEnabled } from '../../../../utility/user';
import { InsufficientCreditsAlertIcon, UnifiedEliteIcon, UnifiedFeaturedIcon } from '../../../../components/svg';
import { CollapseRadio, CollapseStyled, SkipLink, UpSellImage } from './style';
import {
  pageViewUpsellEvent,
  skipListingUpgradeEvent,
  upgradeButtonUpsellClickEvent,
} from '../../../../services/analyticsService';
const Lottie = lazy(() => import('../../../../components/common/lottie/lottie'));
const { Panel } = Collapse;

const UpgradeListingPage = (props) => {
  const listingId = props?.id;
  const { t } = useTranslation();
  const navigate = useRouteNavigate();
  const user = useSelector((state) => state.app.loginUser.user);
  const isUnifiedPostListing = isUnifiedProfolioEnabled({ featureKey: 'postListing' });
  const [disabled, setDisable] = useState(false);
  const [selection, setSelection] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [applyProduct] = useApplyProductMutation();
  const [createCart, _] = useCreateCartMutation();
  const { isMobile, locale, isMultiPlatform } = useSelector((state) => state.app.AppConfig);

  const platformProductSlugs = { bayut: 'hot-listing', dubizzle: 'feature' };

  const {
    data: upsellData,
    isLoading: loading,
    error,
  } = useGetUpsellDetailQuery(
    {
      listing_id: listingId,
      platformProductSlugs,
    },
    { skip: !listingId, refetchOnMountOrArgChange: true },
  );

  const { descriptionMsg, headingMsg, listing, currency, platforms = {} } = upsellData || {};
  useEffect(() => {
    const listingForAnalytics = upsellData?.listing?.listing ?? upsellData?.listing;
    if (listingForAnalytics && user) {
      pageViewUpsellEvent(user, listingForAnalytics);
    }
  }, [upsellData, user]);
  useEffect(() => {
    if (upsellData && platforms && typeof platforms === 'object') {
      setSelection(() => {
        const data = {};
        Object.keys(platforms).forEach((pl) => {
          data[pl] = (platforms[pl]?.products || []).map((e) => {
            const requiredCredits = e?.requiredCredits?.[e?.activeDuration] || 0;
            const availableCredits = platforms?.[pl]?.availableCredits || 0;
            const isAffordable = requiredCredits <= availableCredits;
            return { ...e, selected: isUnifiedPostListing ? isAffordable : true };
          });
        });
        return data;
      });
    }
  }, [upsellData, isUnifiedPostListing]);

  useEffect(() => {
    const hasSelectedProducts = Object.values(selection || {}).some((products) =>
      products?.some((product) => product?.selected),
    );
    setDisable(!hasSelectedProducts);
  }, [selection]);

  const getTotal = (currency) => {
    return Object.values(selection || {})?.reduce((total, platform) => {
      platform?.forEach((prod) => {
        if (prod?.selected) {
          currency === tenantConstants.CURRENCY
            ? (total += prod?.price?.[prod?.activeDuration] || 0)
            : (total += prod?.requiredCredits?.[prod?.activeDuration] || 0);
        }
      });
      return total;
    }, 0);
  };

  const getRequiredCredits = (platform) => {
    let total = 0;
    selection?.[platform]?.forEach((prod) => {
      total += prod?.requiredCredits[prod?.activeDuration] || 0;
    });
    return total;
  };

  const getMissingCredits = (platform) => {
    let total = 0;
    const availableCredits = platforms?.[platform]?.availableCredits || 0;
    const required = getRequiredCredits(platform) || 0;

    if (required > availableCredits) {
      total = required - availableCredits;
    }
    return total;
  };

  const applyProducts = async () => {
    setButtonLoading(true);
    const selectedProducts = Object.values(selection).flatMap((platform) =>
      platform.filter((item) => item?.selected === true),
    );
    const selectedIds = selectedProducts.map((item) => item.id);
    const appliedProductTitle = selectedProducts.map((item) => item.appliedTitle);
    const payload = {
      product_ids: selectedIds,
    };
    const response = await applyProduct({ listingId, body: payload });
    upgradeButtonUpsellClickEvent(user, response, appliedProductTitle, response?.data?.listing);
    if (response) {
      setButtonLoading(false);
      if (response.error) {
        notification.error(response.error);
      } else {
        notification.success(t('Listing Updated Successfully'));
        navigate('/listings');
      }
    }
  };

  const createUpsellCart = async () => {
    setButtonLoading(true);
    const total_credits = Object.keys(selection || {}).reduce((acc, platform) => acc + (getMissingCredits(platform) || 0), 0);

    const rows = [];
    const weights = [];
    Object.keys(selection || {}).forEach((platform) => {
      if (getMissingCredits(platform) <= 0) return;
      selection[platform]
        ?.filter((item) => item?.selected)
        ?.forEach((item) => {
          const cup = platforms?.[platform]?.creditUnitPrice;
          const w =
            currency === tenantConstants.CURRENCY
              ? cup
                ? (item?.price?.[item?.activeDuration] || 0) / cup
                : item?.requiredCredits?.[item?.activeDuration] || 0
              : item?.requiredCredits?.[item?.activeDuration] || 0;
          rows.push({ item });
          weights.push(Math.max(0, w));
        });
    });

    const quantities = allocateCartLineQuantities(weights, Math.max(0, Math.round(Number(total_credits) || 0)));
    const listingExpiryDays =
      rows[0]?.item?.activeDuration ||
      listing?.listingExpiryDays ||
      listing?.listing?.expiry_days ||
      30;

    const cart = {
      total_credits,
      listing_expiry_days: listingExpiryDays,
      purchased_full_credits: currency !== 'Credits',
      source: getAppSource(),
      listing_id: listingId,
      cart_details_attributes: rows.map((row, i) => ({
        item_id: row.item?.id,
        item_type: 'Product',
        source: getAppSource(),
        quantity: quantities[i],
        required_credits: quantities[i],
      })),
    };
    const response = await createCart(cart);
    if (!response?.error) {
      setButtonLoading(false);
      if (response) {
        navigate(`/checkout?cart_id=${response?.data?.cart?.id}&upgrade_listing=true`, {
          state: {
            disposition: listing?.disposition?.slug,
            status: listing?.status?.slug,
            interactedFrom: 'upsell',
          },
        });
      }
    }
  };

  // Leaves without applying anything, whatever is selected — no apply or cart call, so no credits are spent.
  const handleSkip = () => {
    skipListingUpgradeEvent(user, listingId);
    navigate('/listings');
  };

  const handleSubmit = async () => {
    if (isUnifiedPostListing) {
      applyProducts();
      return;
    }
    if (
      currency === tenantConstants.CURRENCY ||
      Object.keys(selection).reduce((acc, platform) => (acc += getMissingCredits(platform) || 0), 0) > 0
    ) {
      createUpsellCart();
    } else {
      applyProducts();
    }
  };

  const currencyHandler = (prod, currency, availableCredits) => {
    if (t(currency) === t(tenantConstants.CURRENCY)) {
      return (
        <div className="fs12 color-gray-lightest">
          {prod?.price[prod?.activeDuration]} {tenantConstants.CURRENCY_SYMBOL()} {t('Required')}
        </div>
      );
    } else {
      return (
        <div className="fs12 color-gray-lightest">
          {prod?.requiredCredits[prod?.activeDuration]}
          {`/${formatPrice(availableCredits)}`} {t('Credits')}
        </div>
      );
    }
  };

  const handleCheck = (plat, prod) => {
    if (isUnifiedPostListing) {
      const requiredCredits = prod?.requiredCredits?.[prod?.activeDuration] || 0;
      const availableCredits = platforms?.[plat]?.availableCredits || 0;
      if (requiredCredits > availableCredits) {
        return;
      }
    }
    setSelection((prevSelection) => {
      const updatedSelection = { ...prevSelection };

      if (!updatedSelection[plat]) {
        updatedSelection[plat] = [];
      }

      const productIndex = updatedSelection[plat].findIndex((e) => e.slug === prod.slug);

      if (productIndex !== -1) {
        updatedSelection[plat][productIndex].selected = !updatedSelection[plat][productIndex].selected;
      } else {
        updatedSelection[plat].push({ ...prod, selected: true });
      }

      const hasSelectedProducts = Object.values(updatedSelection).some((products) =>
        products.some((product) => product.selected),
      );
      setDisable(!hasSelectedProducts);

      return updatedSelection;
    });
  };

  const iconMapping = {
    bayut: locale === 'ar' ? 'BayutLogoAr' : 'BayutLogoEn',
    dubizzle: locale === 'ar' ? 'DubizzleLogoAr' : 'DubizzleLogo',
    unified: locale === 'ar' ? 'BayutLogoAr' : 'BayutLogoEn',
  };
  const unifiedUpsellCopy = {
    elite: {
      heading: 'Upgrade to Elite',
      description: 'Reach 30X more buyers',
    },
    featured: {
      heading: 'Upgrade to Featured',
      description: 'Reach 10X more buyers',
    },
  };
  const renderUnifiedUpsellIcon = (type) => (type === 'elite' ? <UnifiedEliteIcon /> : <UnifiedFeaturedIcon />);

  const getUnifiedUpsellType = (product) => {
    const slug = (product?.slug || '').toLowerCase();
    if (slug === 'signature-listing' || slug === 'elite') {
      return 'elite';
    }
    if (slug === 'hot-listing' || slug === 'feature') {
      return 'featured';
    }
    const blob = `${product?.name || ''} ${product?.title || ''}`.toLowerCase();
    if (blob.includes('elite')) {
      return 'elite';
    }
    if (blob.includes('featured') || blob.includes('feature')) {
      return 'featured';
    }
    return null;
  };

  const unifiedOptions = useMemo(() => {
    if (!isUnifiedPostListing) {
      return null;
    }
    const options = {
      elite: { totalCredits: 0, canAfford: true, exists: false },
      featured: { totalCredits: 0, canAfford: true, exists: false },
    };

    Object.keys(platforms || {}).forEach((plat) => {
      const availableCredits = platforms?.[plat]?.availableCredits || 0;
      (platforms?.[plat]?.products || []).forEach((prod) => {
        const upsellType = getUnifiedUpsellType(prod);
        if (!upsellType) return;
        const requiredCredits = prod?.requiredCredits?.[prod?.activeDuration] || 0;
        options[upsellType].exists = true;
        options[upsellType].totalCredits += requiredCredits;
        options[upsellType].canAfford = options[upsellType].canAfford && requiredCredits <= availableCredits;
      });
    });

    return options;
  }, [isUnifiedPostListing, platforms]);

  const unifiedCreditLow =
    !!isUnifiedPostListing &&
    !!unifiedOptions &&
    ((unifiedOptions?.elite?.exists && !unifiedOptions?.elite?.canAfford) ||
      (unifiedOptions?.featured?.exists && !unifiedOptions?.featured?.canAfford));

  const unifiedAvailableCredits = useMemo(() => {
    if (!isUnifiedPostListing || !platforms) {
      return 0;
    }
    return Object.keys(platforms || {}).reduce((total, plat) => total + (platforms?.[plat]?.availableCredits || 0), 0);
  }, [isUnifiedPostListing, platforms]);

  // Case2 + Case3 requirement: if credits are low for ANY option, disable BOTH options.
  const unifiedDisableAllOptions = isUnifiedPostListing && unifiedCreditLow;

  const unifiedSelectedType = useMemo(() => {
    if (!isUnifiedPostListing) return null;
    if (unifiedDisableAllOptions) return null;
    const eliteSelected = Object.keys(selection || {}).some((pl) =>
      (selection?.[pl] || []).some((p) => getUnifiedUpsellType(p) === 'elite' && p?.selected),
    );
    const featuredSelected = Object.keys(selection || {}).some((pl) =>
      (selection?.[pl] || []).some((p) => getUnifiedUpsellType(p) === 'featured' && p?.selected),
    );
    if (eliteSelected) return 'elite';
    if (featuredSelected) return 'featured';
    return null;
  }, [isUnifiedPostListing, selection, unifiedDisableAllOptions]);

  useEffect(() => {
    if (!isUnifiedPostListing || !unifiedOptions) return;
    if (unifiedDisableAllOptions) return;
    if (unifiedSelectedType) return;

    const defaultType = unifiedOptions?.elite?.exists ? 'elite' : 'featured';
    // select default type across platforms where affordable
    setSelection((prev) => {
      const next = {};
      Object.keys(prev || {}).forEach((plat) => {
        const availableCredits = platforms?.[plat]?.availableCredits || 0;
        next[plat] = (prev?.[plat] || []).map((p) => {
          const pType = getUnifiedUpsellType(p);
          if (!pType) return p;
          const requiredCredits = p?.requiredCredits?.[p?.activeDuration] || 0;
          const isAffordable = requiredCredits <= availableCredits;
          return { ...p, selected: pType === defaultType && isAffordable };
        });
      });
      return next;
    });
  }, [isUnifiedPostListing, unifiedOptions, unifiedDisableAllOptions, unifiedSelectedType, platforms]);

  const handleUnifiedSelect = (type) => {
    if (!isUnifiedPostListing || !unifiedOptions) return;
    if (unifiedDisableAllOptions) return;
    setSelection((prev) => {
      const next = {};
      Object.keys(prev || {}).forEach((plat) => {
        const availableCredits = platforms?.[plat]?.availableCredits || 0;
        next[plat] = (prev?.[plat] || []).map((p) => {
          const pType = getUnifiedUpsellType(p);
          if (!pType) return p;
          const requiredCredits = p?.requiredCredits?.[p?.activeDuration] || 0;
          const isAffordable = requiredCredits <= availableCredits;
          return { ...p, selected: pType === type && isAffordable };
        });
      });
      return next;
    });
  };

  const unifiedUpsellState = useMemo(() => {
    const state = {
      elite: { exists: false, canAfford: true },
      featured: { exists: false, canAfford: true },
    };
    if (!isUnifiedPostListing) {
      return state;
    }

    Object.keys(selection || {}).forEach((plat) => {
      const availableCredits = platforms?.[plat]?.availableCredits || 0;
      (selection?.[plat] || []).forEach((prod) => {
        const upsellType = getUnifiedUpsellType(prod);
        if (!upsellType) {
          return;
        }
        const requiredCredits = prod?.requiredCredits?.[prod?.activeDuration] || 0;
        state[upsellType].exists = true;
        state[upsellType].canAfford = state[upsellType].canAfford && requiredCredits <= availableCredits;
      });
    });

    return state;
  }, [selection, platforms, isUnifiedPostListing]);

  const showUnifiedLowCreditBanner =
    isUnifiedPostListing && unifiedCreditLow;

  const panelDesc = Object.entries(selection)
    ?.map(([platform]) => {
      const products = upsellData?.platforms?.[platform]?.products ?? [];
      const productNames = products
        .map((prod) => prod.name)
        .filter(Boolean)
        .join(', ');

      return productNames ? `${t(productNames)} ${t('on')} ${t(capitalizeFirstLetter(platform))}` : null;
    })
    ?.filter(Boolean)
    ?.join(` ${t('and')} `);

  const posted =
    useMemo(() => {
      return Object.keys(platforms || {}).reduce((result, plat) => {
        return result && platforms?.[plat]?.isPosted;
      }, false);
    }, [platforms]) || false;

  const allProductsEmpty =
    useMemo(() => {
      return Object.keys(platforms || {}).every((plat) => (platforms?.[plat]?.products || []).length === 0);
    }, [platforms]) || false;

  return user?.is_credit_user ? (
    <Main>
      <div style={{ maxWidth: 800, marginInline: 'auto' }}>
        {loading ? (
          <Card bodyStyle={{ padding: isMobile ? 16 : '24px 40px 32px' }}>
            <Space size={16} direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
              <Skeleton type="image" active />
              <Skeleton type="title" active />
              {[1, 2, 3].map((e) => (
                <Skeleton key={e} type="button" style={{ height: 90 }} size={'large'} block active />
              ))}
            </Space>
          </Card>
        ) : error ? (
          <EmptyState title={''} message={error} />
        ) : upsellData ? (
          <>
            <Card bodyStyle={{ padding: isMobile ? 16 : '24px 40px 32px', marginBottom: isMobile && '20px' }}>
              {tenantConstants.SHOW_UPSELL_SKIP && (
                <SkipLink type="button" onClick={handleSkip} disabled={buttonLoading}>
                  {t('Skip')}
                </SkipLink>
              )}
              <div className={cx('color-gray-lightest text-center ', isMobile ? 'mb-16' : 'mb-24')}>
                <UpSellImage>
                  <Suspense fallback={<Spinner />}>
                    <Lottie animationData={isMobile ? UpsellMobile : UpsellDesktop} />
                  </Suspense>
                </UpSellImage>

                <Heading style={{ color: tenantTheme['base-color'] }} as={isMobile ? 'h4' : 'h2'} className="mb-4">
                  {t(headingMsg)}
                </Heading>
                {t(descriptionMsg)}
                {isMultiPlatform &&
                  ` ${t('on')} ${Object.keys(platforms || {})
                    .filter((platform) => platforms[platform]?.isPosted)
                    .map((platform) => t(capitalizeFirstLetter(platform)))
                    .join(` ${t('and')} `)}`}
              </div>
              {Object.values(selection)?.flatMap((platform) => platform)?.length > 0 ? (
                <>
                  {isUnifiedPostListing && unifiedOptions ? (
                    <div className={isMobile ? 'mb-16' : 'mb-24'}>
                      {showUnifiedLowCreditBanner && (
                        <Alert
                          style={{ background: '#FFFBFB', border: '1px solid #FDD6D6', borderRadius: 12, marginBottom: 16 }}
                          showIcon
                          type=""
                          icon={<InsufficientCreditsAlertIcon style={{ marginTop: 2 }} />}
                          message={
                            <Flex align="center" justify="space-between" gap="12px" style={{ width: '100%' }}>
                              <div>
                                <div style={{ color: '#000', fontSize: 16, fontWeight: 600 }}>{t('Insufficient credits.')}</div>
                                <div style={{ color: '#000', fontSize: 12, fontWeight: 400 }}>
                                  {t('You are running low on credits. Contact your account manager to get a top-up.')}
                                </div>
                              </div>
                              <div style={{ textAlign: 'end' }}>
                                <div style={{ color: '#9D9D9D', fontSize: 12, fontWeight: 400 }}>
                                  {t('Available Credits')}
                                </div>
                                <div className={isMobile ? 'fz-14 fw-700' : 'fz-16 fw-700'}>{unifiedAvailableCredits}</div>
                              </div>
                            </Flex>
                          }
                        />
                      )}

                      <Flex vertical gap="12px">
                        {(['elite', 'featured'])?.map((key) => {
                          const opt = unifiedOptions?.[key];
                          if (!opt?.exists) return null;
                          const isSelected = unifiedSelectedType === key;
                          const isDisabled = unifiedDisableAllOptions || opt?.canAfford === false;
                          return (
                            <div
                              key={key}
                              role="button"
                              tabIndex={0}
                              onClick={() => handleUnifiedSelect(key)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') handleUnifiedSelect(key);
                              }}
                              style={{
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                borderRadius: 12,
                                border: isSelected ? '1px solid #006169' : '1px solid #E7E7E7',
                                background: isSelected ? '#F4FBFA' : '#FFFFFF',
                                opacity: isDisabled ? 0.5 : 1,
                                padding: 16,
                              }}
                            >
                              <Flex align="center" justify="space-between" gap="12px">
                                <Flex align="center" gap="10px">
                                  <div style={{ lineHeight: 1, flexShrink: 0 }}>{renderUnifiedUpsellIcon(key)}</div>
                                  <div>
                                    <div style={{ fontSize: 16, fontWeight: 600, color: '#000' }}>
                                      {t(unifiedUpsellCopy?.[key]?.heading)}
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 400, color: '#000' }}>
                                      {t(unifiedUpsellCopy?.[key]?.description)}
                                    </div>
                                  </div>
                                </Flex>

                                <Flex align="center" gap="12px">
                                  <div style={{ fontSize: 18, fontWeight: 500, color: '#000', whiteSpace: 'nowrap' }}>
                                    {t('Credits')} <strong>{opt?.totalCredits || 0}</strong>
                                  </div>
                                  <div
                                    style={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: 999,
                                      border: '2px solid #006169',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {isSelected ? (
                                      <div style={{ width: 10, height: 10, borderRadius: 999, background: '#006169' }} />
                                    ) : null}
                                  </div>
                                </Flex>
                              </Flex>
                            </div>
                          );
                        })}
                      </Flex>
                    </div>
                  ) : (
                    <div className={isMobile ? 'mb-4' : 'mb-8'}>
                      <CollapseStyled
                        activeKey={1}
                        style={{ backgroundColor: 'white', borderRadius: '12px' }}
                        className={cx('mb-24', 'listing-card')}
                        bordered={false}
                      >
                        <Panel
                          key={1}
                          showArrow={false}
                          header={
                            <>
                              <TenantComponents.Product
                                title={t('Get More Leads with an Upgrade')}
                                icon={'UpgradeIcon'}
                                iconSize={20}
                                // iconColor={tenantTheme['white']}
                                iconBackgroundColor="transparent"
                                description={`${t('Secure top slots upgrading to')} ${panelDesc}`}
                                price={
                                  <CollapseRadio checked={true}>
                                    {t(currency)} {getTotal(currency)}
                                  </CollapseRadio>
                                }
                              />
                            </>
                          }
                        >
                          <div style={{ marginInlineStart: '35px' }}>
                            <Flex vertical={isMobile} align="center" gap="30px">
                              {Object.keys(platforms)?.length > 0
                                ? Object.keys(platforms)?.map((plat) => {
                                    return platforms[plat]?.products?.map((prod) => {
                                      const selected = selection?.[plat]?.find((e) => e?.slug == prod?.slug)?.selected;
                                      return (
                                        <div key={prod?.slug} className={isMobile ? 'w-100' : ''}>
                                          <Flex align="center" justify={isMobile ? 'space-between' : ''} gap="8px">
                                            <Flex align="center" gap="8px">
                                              <Checkbox value={selected} onChange={() => handleCheck(plat, prod)} />{' '}
                                              <Icon
                                                size="50px"
                                                style={{
                                                  height: '20px',
                                                  marginTop: plat === 'dubizzle' && '-5px',
                                                }}
                                                icon={iconMapping[plat] || undefined}
                                              />
                                            </Flex>
                                            <ProductTag
                                              key={prod?.slug}
                                              icon={prod?.icon}
                                              iconColor={prod?.iconColor}
                                              color={prod?.color}
                                              iconProps={prod?.iconProps}
                                              hideIconInTag={prod?.hideIconInTag}
                                              tagBackgroundColor={prod?.tagBackgroundColor}
                                              size={12}
                                              name={prod?.name ?? prod?.title}
                                            />
                                          </Flex>
                                          <Text>{currencyHandler(prod, currency, platforms[plat]?.availableCredits)}</Text>
                                        </div>
                                      );
                                    });
                                  })
                                : null}
                            </Flex>
                          </div>
                        </Panel>
                      </CollapseStyled>
                    </div>
                  )}
                  <Flex style={{ alignItems: 'center' }}>
                    {!isUnifiedPostListing &&
                      Object.keys(selection).reduce((acc, platform) => (acc += getMissingCredits(platform) || 0), 0) >
                        0 &&
                      currency === 'Credits' && (
                        <Alert
                          style={{ padding: 0, maxWidth: '320px' }}
                          className={isMobile ? 'mb-16' : 'mb-0'}
                          showIcon
                          type=""
                          icon={
                            <Icon
                              className="color-gray-dark"
                              icon="HiInformationCircle"
                              size={16}
                              style={{ marginTop: '2px' }}
                            />
                          }
                          message={
                            <div
                              className={cx('color-gray-light', isMobile ? 'fz-12' : 'fz-14')}
                              style={{ paddingBlockStart: !isMobile && '3px', lineHeight: isMobile && 1.5 }}
                            >
                              {t('Insufficient Credits')}
                              {'.'} {t('Pay')}{' '}
                              <strong className="color-primary">
                                {tenantConstants.CURRENCY_SYMBOL()}{' '}
                                {formatPrice(
                                  Math.round(
                                    Object.keys(selection).reduce(
                                      (acc, platform) =>
                                        (acc +=
                                          getMissingCredits(platform) * platforms[platform]?.creditUnitPrice || 0),
                                      0,
                                    ),
                                  ),
                                )}
                              </strong>{' '}
                              {t('for the additional {{credits}} credits at checkout', {
                                credits: Object.keys(selection).reduce(
                                  (acc, platform) => (acc += getMissingCredits(platform) || 0),
                                  0,
                                ),
                              })}
                            </div>
                          }
                        />
                      )}
                    <ActionButton style={{ alignItems: 'center', flex: 'auto' }}>
                      <div className="color-gray-lightest">
                        <div>{t('Total')}</div>
                        <Typography.Text className="fs16">
                          <strong>
                            {t(currency)} {getTotal(currency)}
                          </strong>
                        </Typography.Text>
                      </div>
                      <Button
                        style={{ maxWidth: isMobile ? '178px' : '190px', width: '100%' }}
                        onClick={() => {
                          handleSubmit();
                        }}
                        type="primary"
                        loading={buttonLoading}
                        disabled={disabled || (isUnifiedPostListing && unifiedDisableAllOptions)}
                      >
                        {t('Continue')}
                      </Button>
                    </ActionButton>
                  </Flex>
                </>
              ) : !posted ? (
                <div className={cx('color-gray-lightest text-center ', isMobile ? 'mb-16' : 'mb-24')}>
                  {t('No Products Applicable')}
                </div>
              ) : (
                posted &&
                allProductsEmpty && (
                  <div className={cx('color-gray-lightest text-center ', isMobile ? 'mb-16' : 'mb-24')}>
                    {t('Products have been successfully applied')}
                  </div>
                )
              )}
            </Card>
          </>
        ) : (
          <Card bodyStyle={{ padding: isMobile ? 16 : '24px 40px 32px' }}>
            <EmptyState type={'table'} hideRetryButton />
          </Card>
        )}
      </div>
    </Main>
  ) : (
    <EmptyState title={''} message={t('You are not authorised to access this page!')} hideRetryButton />
  );
};

export default UpgradeListingPage;
